import React, { useEffect, useState } from 'react';
import { AudioLines, BookOpen, BoxSelect, Circle, ClipboardCopy, ClipboardPaste, Copy, Download, Flag, Gauge, Keyboard, Minus, MousePointer, Play, Plus, Repeat, Scissors, SkipBack, SkipForward, SlidersHorizontal, Square, Wand2, X } from 'lucide-react';

// Named imports only (a namespace import would pull every lucide icon into the bundle).
const ICONS = { AudioLines, BookOpen, BoxSelect, Circle, ClipboardCopy, ClipboardPaste, Copy, Download, Flag, Gauge, Keyboard, Minus, MousePointer, Play, Plus, Repeat, Scissors, SkipBack, SkipForward, SlidersHorizontal, Square, Wand2 };
import { BUILD_STEPS, BUTTONS, MOUSE_MOVES, keyGroups } from '@/coach/guide';
import { keysPlatform, modKey, setKeysPlatform } from '@/coach/shortcuts';

const TABS = [
  { id: 'build', label: 'Build a beat' },
  { id: 'buttons', label: 'Buttons' },
  { id: 'keyboard', label: 'Keyboard' },
  { id: 'mouse', label: 'Mouse moves' },
];

const Key = ({ children }) => (
  <kbd style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 30, height: 28, padding: '0 8px', borderRadius: 6, background: 'var(--ba-text)', border: '1px solid var(--ba-text-2)', borderBottom: '3px solid var(--ba-text-3)', color: 'var(--ba-surface-0)', fontSize: 13, fontWeight: 800, fontFamily: 'inherit', boxShadow: 'var(--ba-shadow-soft)' }}>{children}</kbd>
);

/** Pizzul's Guide: how to use Beat Arranger, in plain words, Windows keys first. */
export const PizzulGuide = ({ isOpen, onClose, touchOnly = false }) => {
  const [tab, setTab] = useState('build');
  const [platform, setPlatform] = useState(keysPlatform);
  useEffect(() => { if (isOpen) setPlatform(keysPlatform()); }, [isOpen]);
  if (!isOpen) return null;
  const tabs = touchOnly ? TABS.filter((t) => t.id !== 'keyboard') : TABS;
  const choosePlatform = (next) => { setKeysPlatform(next); setPlatform(next); };
  const mod = modKey(platform);

  return (
    <div data-testid="pizzul-guide-backdrop" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'var(--ba-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-testid="pizzul-guide" role="dialog" aria-label="Pizzul's Guide"
        style={{ width: 'min(780px, 100%)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', background: 'var(--ba-surface-1)', border: '1px solid var(--ba-line)', borderRadius: 'var(--ba-radius-lg)', boxShadow: 'var(--ba-shadow-popover)', overflow: 'hidden' }}>
        <header style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: '1px solid var(--ba-line)' }}>
          <img src="/pizzul/thinking.png" alt="" style={{ width: 56, height: 56, objectFit: 'contain' }} />
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--ba-text)' }}>Pizzul&apos;s Guide</h2>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--ba-text-2)' }}>Everything Beat Arranger can do, one step at a time.</p>
          </div>
          {!touchOnly && (
            <div role="group" aria-label="Keyboard type" style={{ display: 'flex', borderRadius: 20, border: '1px solid var(--ba-line)', overflow: 'hidden' }}>
              {['windows', 'mac'].map((p) => (
                <button key={p} data-testid={`guide-platform-${p}`} onClick={() => choosePlatform(p)}
                  style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer', background: platform === p ? 'var(--ba-accent)' : 'transparent', color: platform === p ? 'var(--ba-surface-0)' : 'var(--ba-text-2)' }}>
                  {p === 'windows' ? 'Windows' : 'Mac'}
                </button>
              ))}
            </div>
          )}
          <button data-testid="guide-close" onClick={onClose} title="Close (Esc)" aria-label="Close guide"
            style={{ width: 34, height: 34, borderRadius: 17, border: '1px solid var(--ba-line)', background: 'var(--ba-surface-2)', color: 'var(--ba-text-2)', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </header>

        <nav style={{ display: 'flex', gap: 6, padding: '10px 20px', borderBottom: '1px solid var(--ba-line)', flexWrap: 'wrap' }}>
          {tabs.map((t) => (
            <button key={t.id} data-testid={`guide-tab-${t.id}`} onClick={() => setTab(t.id)}
              style={{ padding: '8px 14px', borderRadius: 20, fontSize: 14, fontWeight: 700, cursor: 'pointer', border: `1px solid ${tab === t.id ? 'var(--ba-accent)' : 'var(--ba-line)'}`, background: tab === t.id ? 'var(--ba-accent)' : 'transparent', color: tab === t.id ? 'var(--ba-surface-0)' : 'var(--ba-text-2)' }}>
              {t.label}
            </button>
          ))}
        </nav>

        <div data-testid={`guide-panel-${tab}`} style={{ overflowY: 'auto', padding: '16px 20px 20px' }}>
          {tab === 'build' && (
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
              {BUILD_STEPS.map((step, i) => (
                <li key={step.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 12, borderRadius: 12, background: 'var(--ba-surface-2)', borderLeft: `4px solid ${step.color}` }}>
                  <span style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', background: step.color, color: 'var(--ba-surface-0)', fontWeight: 800 }}>{i + 1}</span>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ba-text)' }}>{step.title}</div>
                    <div style={{ fontSize: 14, lineHeight: 1.45, color: 'var(--ba-text-2)' }}>{step.how}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}

          {tab === 'buttons' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 8 }}>
              {BUTTONS.map((b) => {
                const Icon = ICONS[b.icon] || Circle;
                return (
                  <div key={b.name} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 10, borderRadius: 10, background: 'var(--ba-surface-2)' }}>
                    <span style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--ba-surface-1)', border: '1px solid var(--ba-line)', color: 'var(--ba-accent)' }}><Icon size={16} /></span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ba-text)' }}>{b.name}</div>
                      <div style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--ba-text-2)' }}>{b.what}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'keyboard' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--ba-text-2)' }}>Hold the first key, then tap the next. Showing {platform === 'mac' ? 'Mac' : 'Windows'} keys.</p>
              {keyGroups(mod).map((group) => (
                <section key={group.title}>
                  <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 800, color: 'var(--ba-accent)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{group.title}</h3>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {group.rows.map((row) => (
                      <div key={row.what} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '6px 10px', borderRadius: 8, background: 'var(--ba-surface-2)' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 150 }}>
                          {row.keys.map((k, i) => (
                            <React.Fragment key={k + i}>
                              {i > 0 && <span style={{ color: 'var(--ba-text-3)', fontSize: 13 }}>{row.either ? 'or' : '+'}</span>}
                              <Key>{k}</Key>
                            </React.Fragment>
                          ))}
                        </span>
                        <span style={{ fontSize: 14, color: 'var(--ba-text)' }}>{row.what}</span>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {tab === 'mouse' && (
            <div style={{ display: 'grid', gap: 8 }}>
              {MOUSE_MOVES.map((m) => (
                <div key={m.move} style={{ display: 'flex', gap: 14, alignItems: 'baseline', padding: '8px 12px', borderRadius: 10, background: 'var(--ba-surface-2)' }}>
                  <span style={{ flexShrink: 0, minWidth: 180, fontSize: 14, fontWeight: 800, color: 'var(--ba-text)' }}>{m.move.replace(/^(Ctrl|⌘)/, mod)}</span>
                  <span style={{ fontSize: 14, lineHeight: 1.4, color: 'var(--ba-text-2)' }}>{m.what}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PizzulGuide;
